import { DEFAULT_DATE_LOCALE } from '@lumapps/constants';
import { cleanUpLocaleDateString } from '@lumapps/utils/timezones/cleanUpLocaleDateString';
import { getCurrentTimezone } from '@lumapps/utils/timezones/getCurrentTimezone';
import { getDateInSpecificTimezone } from '@lumapps/utils/timezones/getDateInSpecificTimezone';
import { getTimeZoneOffset } from '@lumapps/utils/timezones/getTimeZoneOffset';
import { Time, TimeZone } from '@lumapps/utils/types/Time';

export interface GetSelectedDateTimeZoneOptions {
    /** timestamp in UTC */
    timestamp?: string;
    /** timezone in the format Europe/Paris */
    timeZone?: string;
    /** offset that the default time will be displayed with */
    defaultHourOffset?: number;
    /** whether we should fallback to current date time when none provided */
    shouldFallbackToCurrentDateTime?: boolean;
}

export interface GetSelectedDateTimeZone {
    date?: Date;
    time?: Time;
    timeZone: TimeZone;
}

/**
 * Get Time object from specified Date or Now.
 * */
export const getDefaultTime = (date?: Date): Time => {
    const now = date ?? new Date();

    return {
        hour: now.getHours(),
        minute: now.getMinutes(),
        seconds: now.getSeconds(),
    };
};

/**
 * Returns the default date time zone to use for the picker
 * @param GetSelectedDateTimeZoneOptions
 * @returns default date time zone
 */
export const getDefaultDateTimeZone = ({ offset = 0 }: { offset?: number }) => {
    const now = new Date();
    now.setHours(now.getHours() + offset);
    const currentTimeZone = getCurrentTimezone();

    return {
        date: now,
        time: getDefaultTime(now),
        timeZone: {
            id: currentTimeZone,
            name: '',
            offset: getTimeZoneOffset({ timeZone: currentTimeZone }),
        },
    };
};

/**
 * Returns the parsed date, time and timezone for the provided timestamp. If either the timestamp or the
 * timezone are null, this function will use the current user's date and timezone.
 * @param GetSelectedDateTimeZoneOptions
 * @returns GetSelectedDateTimeZone
 */
export const getSelectedDateTimeZone = ({
    timestamp,
    timeZone,
    defaultHourOffset,
    shouldFallbackToCurrentDateTime = true,
}: GetSelectedDateTimeZoneOptions): GetSelectedDateTimeZone => {
    if (timestamp && timeZone) {
        try {
            /**
             * In order to retrieve the date, time and timezone for the given timestamp,
             * we need to determine:
             * - The timezone offset (UTC+03:00 for example)
             * - The date of the provided timestamp (for example, 30/04/2022)
             * - The hour and minute of the provided timestamp (12:30)
             *
             * With those three fields, we can calculate and create the necessary objects to
             * split the timestamp into date, time and timezone.
             */
            const timeZoneOffset = getTimeZoneOffset({ timeZone });
            const dateInTimezone = getDateInSpecificTimezone({
                date: timestamp,
                timeZone,
                /** dateStyle: short prints the date as `30/04/2022` */
                options: { dateStyle: 'short' },
            });

            /**
             * In order to retrieve the hour and minute, we convert the given date
             * into a string that includes the timezone transformation.
             * dayTime looks like 25, 14:30, and in order to parse the
             * hour and minute, we remove the first 2 characters, remove the comma and split by ':' and retrieve
             * the tokens.
             */
            const dayTime = getDateInSpecificTimezone({
                date: timestamp,
                timeZone,
                options: {
                    hour12: false,
                    day: '2-digit',
                    timeZone,
                    hour: 'numeric',
                    minute: 'numeric',
                    second: 'numeric',
                },
            });
            const cleanTime = cleanUpLocaleDateString(dayTime);
            const timeArray = cleanTime.split(':');
            const [hourInTimezone, minuteInTimezone, secondInTimezone] = timeArray.map((token) => parseInt(token, 10));

            /**
             * This converts the date string `30/04/2022` to a date object.
             */
            const date = new Date(dateInTimezone);

            return {
                date,
                time: {
                    /**
                     * Since getDateInSpecificTimezone can return 24:30 for time after midnight, but 24:30 is NOT a valid time,
                     * we have to use modulo to make sure the hour is always between 0 and 23
                     */
                    hour: hourInTimezone % 24,
                    minute: minuteInTimezone,
                    seconds: secondInTimezone,
                },
                timeZone: {
                    id: timeZone,
                    name: '',
                    offset: timeZoneOffset,
                },
            };
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error(err);
        }
    }

    const fallbackDateTimeZone = getDefaultDateTimeZone({
        offset: defaultHourOffset,
    });

    if (!shouldFallbackToCurrentDateTime) {
        return {
            timeZone: timeZone
                ? {
                      id: timeZone,
                      name: '',
                      offset: getTimeZoneOffset({ timeZone }),
                  }
                : fallbackDateTimeZone.timeZone,
        };
    }

    /**
     * In the case we do not have a timestamp and a timezone,
     * we default to the current date and the current timezone.
     */
    return fallbackDateTimeZone;
};

/**
 * Returns a timestamp in UTC from a given date, time and timezone
 * @param props - GetSelectedDateTimeZone
 * @returns timestamp in UTC
 */
export const getTimestampFromDateTimeZone = (props: GetSelectedDateTimeZone) => {
    const { timeZone, date, time } = props;

    if (!date || !time) {
        return undefined;
    }

    /**
     * We cannot trust the offest received in timeZone becauce of winter/summer times in each timeZone.
     * Hence why we have to calculate it again each time
     */
    const timeZoneOffset = getTimeZoneOffset({ date, timeZone: timeZone.id, language: DEFAULT_DATE_LOCALE });

    // As we are building a universal timestamp, the local needs to use the default locale to have the right format
    const selectedDate = new Date(
        `${date.toLocaleDateString(DEFAULT_DATE_LOCALE, { dateStyle: 'short' })} ${time.hour}:${time.minute}:${
            time.seconds || 0
        } ${timeZoneOffset}`,
    );

    return selectedDate.toISOString();
};
